import { useState } from 'react';

import {
  Box,
  Paper,
  alpha,
  Tooltip,
  Typography,
  tooltipClasses,
  BottomNavigation,
  BottomNavigationAction,
} from '@mui/material';

import NewScanIcon from 'src/assets/icons/new-scan-icon';
import HomeLayoutIcon from 'src/assets/icons/home-layout-icon';
import GiftLayoutIcon from 'src/assets/icons/gift-layout-icon';
import GroupLayoutIcon from 'src/assets/icons/group-layout-icon';
import SearchLayoutIcon from 'src/assets/icons/serch-layout-icon';

export type Props = {
  pathname: string;
  sliderOpen: boolean;
  onClickBottomNavigation: (newValue: number) => void;
  bottomNavRef: React.RefObject<HTMLDivElement>;
  theme: any;
  showTooltip: any;
  invoiceRef: any;
};

export default function BottomNavigationComponent({
  pathname,
  sliderOpen,
  onClickBottomNavigation,
  bottomNavRef,
  theme,
  showTooltip,
  invoiceRef,
}: Props) {
  const [isHomePressed, setIsHomePressed] = useState(false);
  const [isSearchPressed, setIsSearchPressed] = useState(false);
  const [isScanPressed, setIsScanPressed] = useState(false);
  const [isGiftPressed, setIsGiftPressed] = useState(false);
  const [isMenuPressed, setIsMenuPressed] = useState(false);

  const handleHomePressStart = () => {
    setIsHomePressed(true);
  };

  const handleHomePressEnd = () => {
    setIsHomePressed(false);
  };

  const handleSearchPressStart = () => {
    setIsSearchPressed(true);
  };

  const handleSearchPressEnd = () => {
    setIsSearchPressed(false);
  };

  const handleScanPressStart = () => {
    setIsScanPressed(true);
  };

  const handleScanPressEnd = () => {
    setIsScanPressed(false);
  };

  const handleGiftPressStart = () => {
    setIsGiftPressed(true);
  };

  const handleGiftPressEnd = () => {
    setIsGiftPressed(false);
  };

  const handleMenuPressStart = () => {
    setIsMenuPressed(true);
  };

  const handleMenuPressEnd = () => {
    setIsMenuPressed(false);
  };

  const isNavigationMenuPage = pathname.includes('navigationMenu');
  const isSearchPage = pathname.includes('search') && !pathname.includes('results');
  const isMenuOverlayActive = sliderOpen || isNavigationMenuPage;
  const isHighlightedNavigation = isMenuOverlayActive || isSearchPage;
  const isHomeRoute = pathname.includes('home') && !isMenuOverlayActive;
  const isScanSelected = pathname.includes('invoice') && !isMenuOverlayActive;

  return (
    <Paper
      ref={bottomNavRef}
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: theme.zIndex.drawer,
        boxShadow: !isNavigationMenuPage ? `0px -4px 16px ${theme.palette.blueBrand[400]}33` : `0px -4px 16px #00000033`,
      }}
      data-testid="bottom-navigation-container"
    >
      <BottomNavigation
        onChange={(e, newValue) => {
          onClickBottomNavigation(newValue);
          e.stopPropagation();
        }}
        value={false}
        showLabels
        sx={{
          height: 80,
          backgroundColor:
            isHighlightedNavigation
              ? theme.palette.blueBrand[500]
              : theme.palette.common.white,
          '& .MuiBottomNavigationAction-root': {
            minWidth: 0,
            padding: '0 12px',
            paddingBottom: '8px',
          },
          '@media (max-width:375px)': {
            '& .MuiBottomNavigationAction-root': {
              padding: '0 6px',
              paddingBottom: '8px',
            },
          },
        }}
        data-testid="bottom-navigation"
      >
        <BottomNavigationAction
          value={0}
          icon={
            <HomeLayoutIcon
              highlighted={isHighlightedNavigation}
              pressed={isHomePressed}
            />
          }
          label={
            <Typography
              variant="inputS500"
              color={
                isHighlightedNavigation
                  ? '#EAEEF5'
                  : theme.palette.blueBrand[500]
              }
            >
              Início
            </Typography>
          }
          onTouchStart={handleHomePressStart}
          onTouchEnd={handleHomePressEnd}
          sx={{
            '&:hover': {
              backgroundColor: 'transparent',
            },
            '& .MuiTouchRipple-root': {
              display: 'none',
            },
            '& .MuiSvgIcon-root, & svg': {
              transition: 'all 0.2s ease',
            },
            '&:active .MuiSvgIcon-root, &:active svg': {
              backgroundColor: alpha(
                isHighlightedNavigation
                  ? '#FFFFFF'
                  : '#112645',
                0.2
              ),
              borderRadius: '50%',
            },
          }}
          data-testid="bottom-nav-home"
        />
        <BottomNavigationAction
          value={1}
          icon={
            <SearchLayoutIcon
              highlighted={isMenuOverlayActive}
              pressed={isSearchPressed}
              searchPage={isSearchPage && !isMenuOverlayActive}
              resultPage={pathname.includes('results')}
            />
          }
          label={
            <Typography
              variant="inputS500"
              color={
                isHighlightedNavigation
                  ? '#EAEEF5'
                  : theme.palette.blueBrand[500]
              }
            >
              Buscar
            </Typography>
          }
          onTouchStart={handleSearchPressStart}
          onTouchEnd={handleSearchPressEnd}
          sx={{
            '&:hover': {
              backgroundColor: 'transparent',
            },
            '& .MuiTouchRipple-root': {
              display: 'none',
            },
            '& .MuiSvgIcon-root, & svg': {
              transition: 'all 0.2s ease',
            },
            '&:active .MuiSvgIcon-root, &:active svg': {
              backgroundColor: alpha(
                isHighlightedNavigation
                  ? '#FFFFFF'
                  : '#112645',
                0.2
              ),
              borderRadius: '50%',
            },
          }}
          data-testid="bottom-nav-search"
        />
        <Tooltip
          title={
            <Typography variant="paragraphP700" color="yellowBrand.0">
              Escaneie suas notas aqui
            </Typography>
          }
          slotProps={{
            popper: {
              sx: {
                [`& .${tooltipClasses.tooltip}`]: {
                  backgroundColor: theme.palette.blueBrand[400],
                  py: 6,
                  px: 10,
                  borderRadius: 5,
                },
                pointerEvents: 'none',
                zIndex: 100,
              },
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, -20],
                  },
                },
              ],
            },
            arrow: {
              sx: {
                color: theme.palette.blueBrand[400],
              },
            },
          }}
          open={!!showTooltip}
          placement="top-start"
          arrow
        >
          <BottomNavigationAction
            ref={invoiceRef}
            value={2}
            icon={
              <Box
                sx={{
                  position: 'relative',
                  display: 'grid',
                  placeItems: 'center',
                  width: 48,
                  height: 48,
                  zIndex: 2,
                  transform: isHomeRoute ? 'translateY(-8px)' : 'none',
                  transition: 'transform 0.3s ease',

                  ...((isHomeRoute || pathname.includes('results')) && {
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      width: 57,
                      height: 58,
                      borderRadius: '50%',
                      backgroundColor: (() => {
                        if (pathname.includes('results')) {
                          return isScanPressed ? '#FBE0BE' : 'transparent';
                        }
                        return isScanPressed ? '#FBE0BE' : '#F6BD74';
                      })(),
                      top: '50%',

                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      zIndex: -1,
                      transition:
                        'background-color .2s ease, transform .2s ease, box-shadow .2s ease',
                      boxShadow: (() => {
                        if (pathname.includes('results')) {
                          return isScanPressed ? '0 10px 22px rgba(0,0,0,.10)' : 'none';
                        }
                        return '0 10px 22px rgba(0,0,0,.10)';
                      })(),
                    },
                  }),
                }}
              >
                <NewScanIcon
                  selected={isScanSelected}
                  none={!isMenuOverlayActive && !isSearchPage}
                  highlighted={isHighlightedNavigation}
                  pressed={isScanPressed}
                />
              </Box>
            }
            label={
              <Typography
                variant="inputS500"
                color={
                    isHighlightedNavigation
                    ? '#EAEEF5'
                    : theme.palette.blueBrand[500]
                }
                sx={{
                  fontWeight: isHomeRoute ? 700 : undefined,
                }}
              >
                Escanear
              </Typography>
            }
            sx={{
              '&:hover': {
                backgroundColor: 'transparent',
              },
              '& .MuiTouchRipple-root': {
                display: 'none',
              },
              ...(isHighlightedNavigation
                ? {
                  '& .MuiSvgIcon-root, & svg': {
                    transition: 'all 0.2s ease',
                  },
                  '&:active .MuiSvgIcon-root, &:active svg': {
                    backgroundColor: alpha('#FFFFFF', 0.2),
                    borderRadius: '50%',
                  },
                }
                : {}),
            }}
            onTouchStart={handleScanPressStart}
            onTouchEnd={handleScanPressEnd}
            data-testid="bottom-nav-invoice"
          />
        </Tooltip>
        <BottomNavigationAction
          value={3}
          icon={
            <GiftLayoutIcon
              selected={pathname.includes('benefits') && !isMenuOverlayActive}
              highlighted={isHighlightedNavigation}
              pressed={isGiftPressed}
            />
          }
          label={
            <Typography
              variant="inputS500"
              color={
                isHighlightedNavigation
                  ? '#EAEEF5'
                  : theme.palette.blueBrand[500]
              }
            >
              Benefícios
            </Typography>
          }
          sx={{
            '&:hover': {
              backgroundColor: 'transparent',
            },
            '& .MuiTouchRipple-root': {
              display: 'none',
            },
            '& .MuiSvgIcon-root, & svg': {
              transition: 'all 0.2s ease',
            },
            '&:active .MuiSvgIcon-root, &:active svg': {
              backgroundColor: alpha(
                isHighlightedNavigation
                  ? '#FFFFFF'
                  : '#112645',
                0.2
              ),
              borderRadius: '50%',
            },
          }}
          onTouchStart={handleGiftPressStart}
          onTouchEnd={handleGiftPressEnd}
          data-testid="bottom-nav-gift"
        />
        <BottomNavigationAction
          value={4}
          icon={
            <GroupLayoutIcon
              highlighted={isMenuOverlayActive}
              searchPage={isSearchPage}
              pressed={isMenuPressed}
            />
          }
          label={
            <Typography
              variant="inputS500"
              color={(() => {
                if (isMenuOverlayActive) {
                  return '#FBE0BE';
                }
                if (isSearchPage) {
                  return '#EAEEF5';
                }
                return '#112645';
              })()}
            >
              Menu
            </Typography>
          }
          sx={{
            '&:hover': {
              backgroundColor: 'transparent',
            },
            '& .MuiTouchRipple-root': {
              display: 'none',
            },
            '& .MuiSvgIcon-root, & svg': {
              transition: 'all 0.2s ease',
            },
            '&:active .MuiSvgIcon-root, &:active svg': {
              backgroundColor: alpha(
                isHighlightedNavigation
                  ? '#FFFFFF'
                  : '#112645',
                0.2
              ),
              borderRadius: '50%',
            },
          }}
          onTouchStart={handleMenuPressStart}
          onTouchEnd={handleMenuPressEnd}
          data-testid="bottom-nav-group"
        />
      </BottomNavigation>
    </Paper>
  );
}
